/**
 * Нативная авторизация. Все маршруты `/auth/*` живут на core-бинаре
 * (`coreHttp`), а не на основном — это разные процессы за разными
 * nginx-локациями. Токен, выданный core, действителен и на основном API.
 */
import { CORE_BASE_URL, coreHttp } from '@/shared/api';
import { env } from '@/shared/config';

import type {
  GoogleCallbackResponse,
  IdentifyResponse,
  LoginPayload,
  LoginResponse,
  MfaSetupResponse,
  NativeTokenResponse,
  NativeUser,
  RegisterPayload,
} from '../model/types';

/**
 * Тенант без явного выбора. Вход берёт компанию из `/auth/identify`, а
 * регистрации выбирать не из чего — ей остаётся константа из env.
 */
function withTenant<T extends { tenant_slug?: string }>(payload: T) {
  return { ...payload, tenant_slug: payload.tenant_slug || env.tenantSlug };
}

export async function register(
  payload: Omit<RegisterPayload, 'tenant_slug'> & { tenant_slug?: string }
): Promise<NativeTokenResponse> {
  const { data } = await coreHttp.post<NativeTokenResponse>(
    '/auth/register',
    withTenant(payload)
  );

  return data;
}

/**
 * Первый шаг входа: в каких компаниях заведена почта. Каждый вызов идёт в
 * лимит попыток, поэтому зовётся только по кнопке, а не на ввод символа.
 */
export async function identify(email: string): Promise<IdentifyResponse> {
  const { data } = await coreHttp.post<IdentifyResponse>('/auth/identify', {
    email,
  });

  return data;
}

/** При включённой 2FA вместо токенов приходит `mfa_token` — см. `verifyMfa`. */
export async function login(
  payload: Omit<LoginPayload, 'tenant_slug'> & { tenant_slug?: string }
): Promise<LoginResponse> {
  const { data } = await coreHttp.post<LoginResponse>(
    '/auth/login',
    withTenant(payload)
  );

  return data;
}

/**
 * Второй фактор. `code` — 6 цифр из приложения или резервный код из 16
 * символов: бэк сам различает, что пришло.
 */
export async function verifyMfa(payload: {
  mfa_token: string;
  code: string;
}): Promise<NativeTokenResponse> {
  const { data } = await coreHttp.post<NativeTokenResponse>(
    '/auth/mfa/verify',
    payload
  );

  return data;
}

/**
 * Адрес входа через Google. Открывается переходом браузера, не fetch и не
 * popup: бэк ставит защитную куку и сам отправляет на страницу Google.
 */
export function googleLoginUrl(): string {
  return `${CORE_BASE_URL}/auth/google`;
}

/**
 * Обмен `code` и `state`, с которыми Google вернул браузер на фронт. Кука из
 * первого шага уходит благодаря `withCredentials` у coreHttp — без неё бэк
 * отвечает 400 invalid state.
 */
export async function googleCallback(params: {
  code: string;
  state: string;
}): Promise<GoogleCallbackResponse> {
  const { data } = await coreHttp.get<GoogleCallbackResponse>(
    '/auth/google/callback',
    { params }
  );

  return data;
}

/** Выбор компании, если почта Google заведена в нескольких. select_token живёт 5 минут. */
export async function selectGoogleTenant(payload: {
  select_token: string;
  tenant_id: string;
}): Promise<NativeTokenResponse> {
  const { data } = await coreHttp.post<NativeTokenResponse>(
    '/auth/google/select-tenant',
    payload
  );

  return data;
}

export async function fetchMe(): Promise<NativeUser> {
  const { data } = await coreHttp.get<NativeUser>('/auth/me');

  return data;
}

/**
 * Отзывает refresh-токен на бэке. Access продолжает жить до конца своего срока
 * — по устройству он не отзывается, поэтому локальную очистку делает стор.
 */
export async function logout(refreshToken: string): Promise<void> {
  await coreHttp.post('/auth/logout', { refresh_token: refreshToken });
}

/**
 * Установка пароля по инвайту. Токен приходит ссылкой из письма
 * (`/set-password?token=…`), сессию ручка не выдаёт — после успеха ведём на
 * обычный вход.
 */
export async function setPassword(payload: {
  token: string;
  password: string;
}): Promise<void> {
  await coreHttp.post('/auth/set-password', payload);
}

/** Включение 2FA, шаг 1: секрет для приложения-аутентификатора. 409 — уже включена. */
export async function setupMfa(): Promise<MfaSetupResponse> {
  const { data } = await coreHttp.post<MfaSetupResponse>('/auth/mfa/setup');

  return data;
}

/**
 * Включение 2FA, шаг 2: код из приложения. Резервные коды бэк отдаёт один раз
 * и больше не покажет.
 */
export async function confirmMfa(code: string): Promise<string[]> {
  const { data } = await coreHttp.post<{ recovery_codes: string[] }>(
    '/auth/mfa/confirm',
    { code }
  );

  return data.recovery_codes;
}

/** Выключение 2FA по текущему паролю. */
export async function disableMfa(password: string): Promise<void> {
  await coreHttp.delete('/auth/mfa', { data: { password } });
}
